import { create } from 'zustand';
import { Radar, Practice } from '../../domain/entities/RadarTypes';
import type { RadarRepositoryPort } from '../../application/ports/RadarRepositoryPort';
import {
  NotFoundError,
  InfrastructureError,
} from '../../domain/errors/DomainErrors';
import { createRadarRepository } from '../persistence/RepositoryFactory';
import { LocalStorageRadarRepository } from '../persistence/LocalStorageRadarRepository';
import { createPersistenceAdapter } from '../persistence/adapters/RedisAdapter';

interface RadarState {
  // State
  readonly radars: readonly Radar[];
  readonly selectedRadar: Radar | null;
  readonly loading: boolean;
  readonly error: string | null;

  // Add type-safe error handling
  readonly errorType?: 'network' | 'validation' | 'not_found';

  // Only expose operations needed by UI
  fetchRadars: (tenantId: string, teamId: string) => Promise<void>;
  createRadar: (
    tenantId: string,
    teamId: string,
    radar: Omit<Radar, 'id' | 'tenantId' | 'teamId' | 'createdAt' | 'updatedAt'>
  ) => Promise<void>;
  addPractice: (
    tenantId: string,
    teamId: string,
    radarId: string,
    practice: Omit<Practice, 'id' | 'tenantId' | 'teamId'>
  ) => Promise<void>;
  updatePractice: (
    tenantId: string,
    teamId: string,
    radarId: string,
    practice: Omit<Practice, 'tenantId' | 'teamId'>
  ) => Promise<void>;
  deletePractice: (
    tenantId: string,
    teamId: string,
    radarId: string,
    practiceId: string
  ) => Promise<void>;
  deleteRadar: (
    tenantId: string,
    teamId: string,
    radarId: string
  ) => Promise<void>;

  readonly _version: 1; // Helps with future migrations

  // Add TTL for fetched data
  lastFetched: number;
}

class RadarStoreError extends Error {
  constructor(
    message: string,
    public readonly code: string
  ) {
    super(message);
  }
}

export const createRadarStore = (repository: RadarRepositoryPort) =>
  create<RadarState>(set => ({
    radars: [],
    selectedRadar: null,
    loading: false,
    error: null,
    _version: 1,
    lastFetched: 0,

    fetchRadars: async (tenantId: string, teamId: string) => {
      set({ loading: true, error: null });
      try {
        const radars = await repository.findAllByTeam(tenantId, teamId);

        if (radars.length === 0) {
          set({
            loading: false,
            error: 'No radar data available. Please check the data source.',
            errorType: 'not_found',
          });
          return;
        }

        // Always select the first radar when fetching to ensure something is selected
        set(state => {
          const newSelectedRadar = radars[0] || null;

          return {
            radars,
            selectedRadar: newSelectedRadar,
            loading: false,
            lastFetched: Date.now(),
          };
        });
      } catch (err) {
        const { message, type } = mapToStoreError(err);
        console.error(`[RadarStore] Error fetching radars: ${message}`);
        set({ error: message, errorType: type, loading: false });
      }
    },

    createRadar: async (
      tenantId: string,
      teamId: string,
      radar: Omit<
        Radar,
        'id' | 'tenantId' | 'teamId' | 'createdAt' | 'updatedAt'
      >
    ) => {
      set({ loading: true, error: null });
      try {
        const createdRadar = await repository.createRadar(
          tenantId,
          teamId,
          radar
        );
        set(state => ({
          radars: [...state.radars, createdRadar],
          selectedRadar: createdRadar,
          loading: false,
        }));
      } catch (err) {
        const { message, type } = mapToStoreError(err);
        set({ error: message, errorType: type, loading: false });
      }
    },

    addPractice: async (
      tenantId: string,
      teamId: string,
      radarId: string,
      practice: Omit<Practice, 'id' | 'tenantId' | 'teamId'>
    ) => {
      set({ loading: true, error: null });
      try {
        const updatedRadar = await repository.addPractice(
          tenantId,
          teamId,
          radarId,
          practice
        );
        set(state => ({
          radars: updateRadarArray(state.radars, radarId, updatedRadar),
          selectedRadar: updatedRadar,
          loading: false,
        }));
      } catch (err) {
        const { message, type } = mapToStoreError(err);
        set({ error: message, errorType: type, loading: false });
      }
    },

    updatePractice: async (
      tenantId: string,
      teamId: string,
      radarId: string,
      practice: Omit<Practice, 'tenantId' | 'teamId'>
    ) => {
      set({ loading: true, error: null });
      try {
        await repository.updatePractice(
          tenantId,
          teamId,
          radarId,
          practice.id,
          practice
        );
        const radar = await repository.findById(tenantId, teamId, radarId);
        if (!radar) throw new NotFoundError('Radar not found after update');
        set(state => ({
          radars: updateRadarArray(state.radars, radarId, radar),
          selectedRadar: radar,
          loading: false,
        }));
      } catch (err: unknown) {
        if (err instanceof InfrastructureError) {
          set({ error: err.message, loading: false });
        } else {
          const errorMessage =
            err instanceof RadarStoreError
              ? err.message
              : err instanceof Error
                ? err.message
                : 'Failed to update practice';
          set({ error: errorMessage, loading: false });
        }
      }
    },

    deletePractice: async (
      tenantId: string,
      teamId: string,
      radarId: string,
      practiceId: string
    ) => {
      set({ loading: true, error: null });
      try {
        await repository.deletePractice(tenantId, teamId, radarId, practiceId);

        // After deletion, fetch the updated radar
        const radar = await repository.findById(tenantId, teamId, radarId);
        if (!radar)
          throw new NotFoundError('Radar not found after practice deletion');

        set(state => ({
          radars: updateRadarArray(state.radars, radarId, radar),
          selectedRadar: radar,
          loading: false,
        }));
      } catch (err) {
        const { message, type } = mapToStoreError(err);
        set({ error: message, errorType: type, loading: false });
      }
    },

    deleteRadar: async (tenantId: string, teamId: string, radarId: string) => {
      set({ loading: true, error: null });
      try {
        await repository.deleteRadar(tenantId, teamId, radarId);
        set(state => ({
          radars: state.radars.filter(r => r.id !== radarId),
          selectedRadar:
            state.selectedRadar?.id === radarId
              ? state.radars.find(r => r.id !== radarId) || null
              : state.selectedRadar,
          loading: false,
        }));
      } catch (err) {
        const { message, type } = mapToStoreError(err);
        set({ error: message, errorType: type, loading: false });
      }
    },
  }));

// Helper function for immutable updates
const updateRadarArray = (
  radars: readonly Radar[],
  id: string,
  update: Radar
) => radars.map(r => (r.id === id ? Object.freeze(update) : r));

// Error mapping layer
const mapToStoreError = (err: unknown) => {
  if (err instanceof NotFoundError) {
    return { message: err.message, type: 'not_found' as const };
  }
  // Add other error mappings
  return {
    message: (err as Error).message || 'Unknown error',
    type: 'network' as const,
  };
};

// Create an initial store with a fallback repository
// This will be properly initialized when the app starts
const defaultRepository = new LocalStorageRadarRepository(
  createPersistenceAdapter()
);
const radarStoreInstance = createRadarStore(defaultRepository);

// Export the store without directly calling createRadarRepository
// This prevents async issues at import time
export const useRadarStore = radarStoreInstance;

// Initialize the store with the proper repository asynchronously
// This will be called from main.tsx
export const initializeStore = async (): Promise<void> => {
  console.log('[RadarStore] Initializing store with proper repository');
  try {
    const repository = await createRadarRepository();
    console.log('[RadarStore] Successfully created repository');

    // Create a new store with the proper repository
    const newStore = createRadarStore(repository);
    console.log('[RadarStore] Created new store with repository');

    // Replace the store instance with the new one
    Object.assign(radarStoreInstance, newStore);
    console.log('[RadarStore] Successfully replaced store instance');
  } catch (error) {
    console.error(
      '[RadarStore] Failed to initialize radar store with repository:',
      error
    );
    // Store already has fallback repository, so we can continue
  }
};
